import { motion } from "framer-motion";

/**
 * Manifesto — numbered chapters, asymmetrical editorial layout.
 * Sits between hero and product grid.
 */

const CHAPTERS = [
  {
    n: "01",
    title: "You Were Never “Black.”",
    body:
      "The word was placed on us. It was never our own. Before that label existed, Genesis 2:7 had already named us — EarthTone. Written into the ground itself.",
  },
  {
    n: "02",
    title: "You Are EarthTone.",
    body:
      "Every shade of brown, ochre, umber, and clay in your skin is Genesis 2:7 made visible — the same tones the earth wears when the light hits it right.",
  },
  {
    n: "03",
    title: "Formed From The Dust.",
    body:
      "Genesis 2:7 — the Lord God formed man of the dust of the ground. Not from something abstract. From soil. From earth. From what EarthTone people are still made of.",
  },
  {
    n: "04",
    title: "Remember Who You Are.",
    body:
      "The tribes on every continent — the same EarthTone origin, the same melanin, the same Genesis 2:7 story. This is not a trend. This is the foundation.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative bg-[#1a1412] py-28 md:py-40 border-t border-[#362b28]"
      data-testid="manifesto-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="eyebrow mb-6"
            >
              The Manifesto
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif text-[44px] md:text-[68px] leading-[0.95] tracking-tight text-[#e8dcc4]"
            >
              A short story <br />
              <em className="italic text-[#c28b46]">about who you are.</em>
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="text-[15px] md:text-[17px] leading-relaxed text-[#c8b99c] max-w-[46ch]"
            >
              Every shirt in this collection is a sentence in a longer story — a
              declaration written across the chest that says: <em className="text-[#e8dcc4]">I know where I come from.</em>
            </motion.p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-x-10 gap-y-16 md:gap-y-24">
          {CHAPTERS.map((c, i) => {
            const isEven = i % 2 === 0;
            return (
              <motion.article
                key={c.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 1,
                  delay: 0.05 * i,
                  ease: [0.19, 1, 0.22, 1],
                }}
                className={`col-span-12 md:col-span-6 ${
                  isEven ? "md:col-start-1" : "md:col-start-7 md:pt-24"
                }`}
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <div className="flex items-baseline gap-6 md:gap-10">
                  <span className="font-serif italic text-[#c28b46] text-[68px] md:text-[120px] leading-none">
                    {c.n}.
                  </span>
                  <div>
                    <h3 className="font-serif text-[26px] md:text-[38px] leading-tight text-[#e8dcc4] tracking-tight">
                      {c.title}
                    </h3>
                    <p className="mt-4 md:mt-6 text-[15px] md:text-[17px] leading-relaxed text-[#a89b8c] max-w-[42ch]">
                      {c.body}
                    </p>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
          style={{ transformOrigin: "left" }}
          className="hairline mt-24"
        />
      </div>
    </section>
  );
}
